import React, { useState, useEffect } from 'react';
import { getDatabase, ref as dbRef, get, set } from "firebase/database";
import { getAuth } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import '../GlobalSettingsModal.css';
import ColorVault from '../ColorVault';
import BackgroundColorChanger from '../BackgroundColorChanger';

import { titleFonts, textFonts } from '../../utils/fontsHelper'; // ✅ Import aus zentraler Quelle

const fontOptions = titleFonts; // ✅ Verwende zentrale Font-Liste inkl. Voyage
const smallFontOptions = textFonts; // ✅ Verwende zentrale Text-Font-Liste

const DemoGlobalSettingsModal = ({ show, onClose, pageId }) => {
    const defaultBigFont = "Voyage";
    const defaultSmallFont = "Jost";

    const [pageSettings, setPageSettings] = useState({
        name: '',
        description: '',
        customDomain: '',
        isActive: false,
        globalBigFont: defaultBigFont,
        globalSmallFont: defaultSmallFont,
        colors: {
            color1: '#FFFFFF',
            color2: '#000000',
            color3: '#FF0000',
            color4: '#00FF00',
        },
        backgroundColor: '#FFFFFF', // Default background color
    });

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const [previousCustomDomain, setPreviousCustomDomain] = useState('');
    const [selectedFont, setSelectedFont] = useState('');
    const [selectedSmallFont, setSelectedSmallFont] = useState('');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [imageURL, setImageURL] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (!file) {
            console.log("Keine Datei ausgewählt.");
            return;
        }

        setIsUploading(true);

        const storage = getStorage();
        const storagePath = `settingsImages/${pageId}/${file.name}`;
        const imageFileRef = storageRef(storage, storagePath);

        const uploadTask = uploadBytesResumable(imageFileRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
            },
            (error) => {
                console.log(error);
                setIsUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setImageURL(downloadURL);
                    setPageSettings(prevSettings => ({
                        ...prevSettings,
                        livePageImg: downloadURL
                    }));
                    setIsUploading(false);
                    setUploadProgress(0);
                });
            }
        );
    };

    const checkDomainUniqueness = async (domainName) => {
        if (!domainName) return true;
        const publicPageRef = dbRef(db, `/demoPublicPages/${domainName}`);
        const snapshot = await get(publicPageRef);
        return !snapshot.exists();
    };

    const handleSmallFontChange = (font) => {
        setSelectedSmallFont(font);
        setPageSettings(prevSettings => ({
            ...prevSettings,
            globalSmallFont: font
        }));
    };

    const handleColorChange = (colorKey, newValue) => {
        const newColors = { ...pageSettings.colors, [colorKey]: newValue };
        setPageSettings(prevSettings => ({ ...prevSettings, colors: newColors }));
    };

    const handleBackgroundColorChange = (color) => {
        setPageSettings(prevSettings => ({ ...prevSettings, backgroundColor: color }));
    };

    useEffect(() => {
        if (show && currentUser && pageId) {
            const pageRef = dbRef(db, `/demoPages/${currentUser.uid}/${pageId}`);
            get(pageRef).then((snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setPageSettings(data);
                    setImageURL(data.livePageImg || '');
                    setPreviousCustomDomain(data.customDomain);
                    setSelectedFont(data.globalBigFont);
                    setSelectedSmallFont(data.globalSmallFont);
                }
            });
        }
    }, [show, currentUser, pageId, db]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPageSettings(prevSettings => ({
            ...prevSettings,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const saveSettings = async () => {
        if (currentUser && pageId) {
            if (pageSettings.customDomain !== previousCustomDomain) {
                const isUnique = await checkDomainUniqueness(pageSettings.customDomain);
                if (!isUnique) {
                    alert('Dieser Domainname ist bereits vergeben. Bitte wählen Sie einen anderen.');
                    return;
                }
            }

            const updatedSettings = {
                ...pageSettings,
                globalBigFont: selectedFont || defaultBigFont,
                globalSmallFont: selectedSmallFont || defaultSmallFont,
            };
            const pageRef = dbRef(db, `/demoPages/${currentUser.uid}/${pageId}`);

            if (pageSettings.previousCustomDomain && pageSettings.customDomain !== pageSettings.previousCustomDomain) {
                const oldPublicPageRef = dbRef(db, `/demoPublicPages/${pageSettings.previousCustomDomain}`);
                set(oldPublicPageRef, null);
            }

            if (pageSettings.customDomain !== pageSettings.previousCustomDomain) {
                const publicPageRef = dbRef(db, `/demoPublicPages/${pageSettings.customDomain}`);
                await set(publicPageRef, { pageId: pageId, userId: currentUser.uid });
            }

            set(pageRef, updatedSettings).then(() => {
                console.log('Settings updated successfully');
                onClose();
            }).catch(error => {
                console.error('Error updating settings:', error);
            });
        }
    };

    return (
        <div className="modal-background-big">
            <div className="modal-content-big">
                <h2>Page Settings: {pageSettings.name}</h2>
                <div className="modal-content-inner">
                    <div className="modal-content-left">
                        <div className="settings-group">
                            <h3>Custom Logo</h3>
                            <label className="custom-upload-box" htmlFor="imageUpload" style={{ height: '150px', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '300px', padding: '10px' }}>
                                <input
                                    id="imageUpload"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    style={{ display: 'none' }}
                                />
                                {isUploading && (
                                    <div className="spinner-container">
                                        <div className="spinner-circle"></div>
                                        <p>Uploading... {Math.round(uploadProgress)}%</p>
                                    </div>
                                )}
                                {!isUploading && imageURL && (
                                    <img className="uploaded-image-preview" src={imageURL} alt="Vorschau" style={{ maxHeight: '100px' }} />
                                )}
                                {!isUploading && !imageURL && (
                                    <div className="upload-image-preview">
                                        <img src="/img/upload1.svg" alt="Upload Icon" />
                                        <span>Click here to upload your logo</span>
                                    </div>
                                )}
                            </label>
                        </div>
                        <div className="settings-group">
                            <h3>Standard Settings</h3>
                            <label>Page Name</label>
                            <input type="text" name="name" value={pageSettings.name} onChange={handleChange} placeholder="Page Name" />
                            <label>Page Description</label>
                            <textarea name="description" value={pageSettings.description} onChange={handleChange} placeholder="Description" />
                            <label>Page Link</label>
                            <div className="custom-domain-input">
                                <span className="static-text">bonusclub.link/to/</span>
                                <input
                                    type="text"
                                    name="customDomain"
                                    value={pageSettings.customDomain}
                                    onChange={handleChange}
                                    placeholder="Custom Domain"
                                />
                            </div>
                            <label style={{ marginTop: '30px' }}>WhatsApp Button</label>
                            <label className="switch" style={{ marginTop: '12px' }}>
                                <input
                                    type="checkbox"
                                    name="whatsappStatus"
                                    checked={pageSettings.whatsappStatus}
                                    onChange={handleChange}
                                />
                                <span className="slider round"></span>
                            </label>
                            <label>WhatsApp Number (Country Code without +)</label>
                            <input
                                type="text"
                                name="whatsappNumber"
                                value={pageSettings.whatsappNumber}
                                onChange={handleChange}
                                placeholder="491701234567"
                            />
                            <h3>Colors</h3>
                            <BackgroundColorChanger color={pageSettings.backgroundColor} onChange={handleBackgroundColorChange} />
                            <label>Main Colors</label>
                            <ColorVault colors={pageSettings.colors} onColorChange={handleColorChange} />
                        </div>
                    </div>
                    <div className="modal-content-right">
                        <p className="select-font-text" style={{ marginBottom: '10px' }}>Main Font</p>
                        <div className="settings-fonts">
                            <div className="font-grid">
                                {fontOptions.map(font => (
                                    <div key={font} id={font.toLowerCase()} className={`font-option ${selectedFont === font ? 'selected' : ''}`} onClick={() => setSelectedFont(font)} style={{ fontFamily: font }}>{font}</div>
                                ))}
                            </div>
                        </div>
                        <p className="select-font-text" style={{ marginBottom: '10px' }}>Secondary Font</p>
                        <div className="settings-fonts">
                            <div className="font-grid">
                                {smallFontOptions.map(font => (
                                    <div key={font} id={font.toLowerCase()} className={`font-option ${selectedSmallFont === font ? 'selected' : ''}`} onClick={() => handleSmallFontChange(font)} style={{ fontFamily: font }}>{font}</div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-buttons">
                    <button onClick={onClose}>Close</button>
                    <button id="save-button" onClick={saveSettings}>Save</button>
                </div>
            </div>
        </div>
    )
}

export default DemoGlobalSettingsModal;
